<script setup lang="ts">
/**
 * DonateGoalProgress — barre de progression d'un objectif de collecte.
 *
 * Avant ce composant, les 4 pages de don
 * (donate/[id], pay/[slug], campaigns/donate/[id], c/[slug]/payment)
 * dupliquaient quasiment à l'identique ce bloc HTML (~70 lignes par
 * page, mobile + desktop), avec des variantes mineures de wording.
 *
 * Le composant est pur (props in, pas d'événements out) : on lui passe
 * raised/goal en euros et une couleur primaire, il rend la barre.
 *
 * Variantes supportées :
 *  - `variant="compact"` : version mobile (utilisée 2x par page : desktop
 *    avec wrapper card + mobile dans la grille)
 *  - `showDaysRemaining` : affiche le compteur "X jours restants" si une
 *    date de fin est fournie (cagnottes + payment-link)
 *  - Si `goalAmount` est null/0, le composant retourne `null` (caller
 *    n'a pas à wrapper en v-if).
 */
import { computed } from "vue";

const { t } = useI18n();

interface Props {
  /** Montant déjà récolté (€) */
  raisedAmount: number;
  /** Objectif visé (€). Si null/0, le composant ne s'affiche pas. */
  goalAmount: number | null;
  /** Formatte un montant en string locale (ex: "1 234 €"). */
  formatAmount: (amount: number) => string;
  /** Couleur primaire de la barre (hex). Default bleu Donoor. */
  primaryColor?: string | null;
  /**
   * Nombre de jours restants avant la fin de la cagnotte / objectif.
   * Affiché à côté du pourcentage si > 0. Utiliser `null` pour masquer.
   */
  daysRemaining?: number | null;
  /** Nombre de participants/donateurs — affiché si > 0. */
  donorCount?: number;
}

const props = withDefaults(defineProps<Props>(), {
  primaryColor: null,
  daysRemaining: null,
  donorCount: 0,
});

/** Pourcentage atteint, plafonné à 100%. */
const progressPercentage = computed(() => {
  if (!props.goalAmount || props.goalAmount <= 0) return 0;
  return Math.min((props.raisedAmount / props.goalAmount) * 100, 100);
});

const resolvedColor = computed(() => props.primaryColor || "#3B82F6");

/** Affichage conditionnel : pas d'objectif = pas de barre. */
const shouldRender = computed(
  () => props.goalAmount !== null && props.goalAmount > 0,
);
</script>

<template>
  <div
    v-if="shouldRender"
    class="p-4 bg-gray-50 rounded-xl"
  >
    <div class="flex justify-between items-end mb-2">
      <span class="text-base font-bold text-gray-900">
        {{ formatAmount(raisedAmount) }}
      </span>
      <span class="text-gray-500 text-sm">
        {{ formatAmount(goalAmount!) }}
      </span>
    </div>

    <div class="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
      <div
        class="h-full rounded-full transition-all duration-500"
        :style="{
          width: `${progressPercentage}%`,
          backgroundColor: resolvedColor,
        }"
      />
    </div>

    <div
      class="text-xs text-gray-500 mt-2 flex items-center justify-between gap-2"
    >
      <span>{{ t('don.progressionObjectif.pourcentageAtteint', { pourcentage: progressPercentage.toFixed(0) }) }}</span>

      <span
        v-if="donorCount > 0"
        class="text-gray-400"
      >
        {{ t('don.progressionObjectif.participants', donorCount) }}
      </span>

      <span
        v-if="daysRemaining !== null && daysRemaining > 0"
        class="text-gray-400"
      >
        {{ t('don.progressionObjectif.joursRestants', daysRemaining) }}
      </span>
    </div>
  </div>
</template>
